import { Controller } from "@hotwired/stimulus"

// Polls a statement's processing status and advances the fast_init new-format
// step checklist until the validation page is ready, then redirects.
export default class extends Controller {
  static targets = ["step", "error", "errorMessage"]

  static values = {
    statusUrl: String,
    redirectUrl: String,
    previewUrl: String,
    refreshInterval: { type: Number, default: 2000 }
  }

  connect() {
    this.render(1) // file uploaded
    this.timeoutId = setTimeout(() => this.poll(), 400)
  }

  disconnect() {
    if (this.timeoutId) clearTimeout(this.timeoutId)
  }

  async poll() {
    try {
      const response = await fetch(this.statusUrlValue, { headers: { Accept: "application/json" } })
      if (response.ok) {
        const data = await response.json()
        this.handle(data)
        if (data.status === "pending" || data.status === "processing") {
          this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
        }
      } else {
        this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
      }
    } catch (error) {
      this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
    }
  }

  handle(data) {
    const lastStep = this.stepTargets.length - 1

    switch (data.status) {
      case "pending":
        this.render(1)
        break
      case "processing":
        this.render(data.progress >= 100 ? 4 : 3)
        break
      case "completed":
        this.render(lastStep)
        this.finish(this.redirectUrlValue)
        break
      case "preview":
        this.render(lastStep)
        this.finish(this.previewUrlValue || this.redirectUrlValue)
        break
      default:
        this.fail(data.error_message)
    }
  }

  // Marks steps before `completed` as done, the one at `completed` as active,
  // and the rest as pending.
  render(completed) {
    this.stepTargets.forEach((step, index) => {
      const icon = step.querySelector("i")
      step.classList.remove("text-gray-400", "text-gray-900", "text-indigo-700", "font-medium")
      icon.className = "w-5 text-center"

      if (index < completed) {
        step.classList.add("text-gray-900")
        icon.classList.add("fas", "fa-check-circle", "text-green-500")
      } else if (index === completed) {
        step.classList.add("text-indigo-700", "font-medium")
        icon.classList.add("fas", "fa-spinner", "fa-spin", "text-indigo-600")
      } else {
        step.classList.add("text-gray-400")
        icon.classList.add("fas", "fa-circle", "text-xs", "text-gray-300")
      }
    })
  }

  finish(url) {
    if (url) setTimeout(() => { window.location.href = url }, 1000)
  }

  fail(message) {
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
    if (this.hasErrorMessageTarget && message) this.errorMessageTarget.textContent = message
  }
}
